<template>
  <div :style="{ marginTop: '20px', paddingBottom: '80px' }">

    <div v-if="!loading" style="text-align: left; color: #666;" class="d-flex align-items-center justify-content-center">
      <form @submit.prevent="save_config">
        <!-- yard-only provider (no telematics): support-controlled flags, shown for context only -->
        <div v-if="isYardOnly" class="tc-sos-yard-only">
          <span class="tc-sos-badge">Yard-only provider</span>
          <span>Your company has no vehicle tracking; SOS requests start from a pin dropped on the driver link or the Send SOS page.</span>
        </div>
        <table width="90%" style="font-size: 18px; font-weight: bold" cellpadding="10px">
          <tr data-field="bus_sos_enabled">
            <td align="right" width="500">Enabled</td>
            <td width="800"><b-form-select v-model="model.bus_sos_enabled" :options="boolean_options" style="width:100px"></b-form-select></td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr data-field="bus_sos_email_recipients">
            <td align="right" width="500">Email Recipients</td>
            <td width="800"><b-form-input v-model="model.bus_sos_email_recipients" type="text"></b-form-input></td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr data-field="bus_sos_sms_recipients">
            <td align="right" width="500">SMS Recipients</td>
            <td width="800"><b-form-input v-model="model.bus_sos_sms_recipients" type="text"></b-form-input></td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr data-field="bus_sos_max_miles_away">
            <td align="right" width="500">Range In Miles</td>
            <td width="800">
              <b-form-input v-model="model.bus_sos_max_miles_away" type="text" :state="numericState('bus_sos_max_miles_away')" @input="validateNumeric('bus_sos_max_miles_away', $event, 0, 500)"></b-form-input>
              <b-form-invalid-feedback :state="numericState('bus_sos_max_miles_away')">{{ numericFeedback('bus_sos_max_miles_away') }}</b-form-invalid-feedback>
            </td>
            <td width="250">&nbsp;</td>
          </tr>
          <!-- a yard-only company has no vehicles to tag-match -->
          <tr v-if="!isYardOnly" data-field="bus_sos_include_all_vehicles_with_matching_tag">
            <td align="right" width="500">Include Vehicles Matching Tag</td>
            <td width="800"><b-form-input v-model="model.bus_sos_include_all_vehicles_with_matching_tag" type="text" :state="filterState('bus_sos_include_all_vehicles_with_matching_tag')" @input="validateFilter('bus_sos_include_all_vehicles_with_matching_tag', $event)"></b-form-input>
                <b-form-invalid-feedback :state="filterState('bus_sos_include_all_vehicles_with_matching_tag')">{{ filterFeedback('bus_sos_include_all_vehicles_with_matching_tag') }}</b-form-invalid-feedback></td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr data-field="bus_sos_send_to_other_companies">
            <td align="right" width="500">Send To Other Companies</td>
            <td width="800"><b-form-select v-model="model.bus_sos_send_to_other_companies" :options="boolean_options" style="width:100px"></b-form-select></td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr data-field="bus_sos_receive_from_other_companies">
            <td align="right" width="500">Notify Me Of Nearby SOS</td>
            <td width="800"><b-form-select v-model="model.bus_sos_receive_from_other_companies" :options="boolean_options" style="width:100px"></b-form-select></td>
            <td width="250" style="font-weight: normal; font-size: 13px; color: #6e7e8f;">Get alerted when another company's SOS is near your yards or vehicles.</td>
          </tr>
        </table>


      </form>

      <div v-if="isBusSosEnabled" style="margin-top: 24px; max-width: 760px; text-align: left;">
        <hr/>
        <div style="font-size: 18px; font-weight: bold; margin-bottom: 6px;">Driver SOS Link</div>
        <div style="font-size: 14px; font-weight: normal; color: #666; margin-bottom: 10px;">
          A page drivers open on their phone to send an SOS from a map pin. Pick a link name, then share the link with your drivers.
          Changing the name later disables the old link.
        </div>
        <div class="d-flex align-items-center" style="gap: 8px; flex-wrap: wrap; font-weight: normal;">
          <span style="color: #6e7e8f;">https://web.fleet-integrations.com/#/sos/</span>
          <b-form-input v-model="slug_input" placeholder="e.g. anderson-coach" style="max-width: 260px;" @input="slug_input = ($event || '').toLowerCase()"></b-form-input>
          <b-button size="sm" variant="primary" @click="save_sos_slug" :disabled="sos_slug_loading || !slug_input">
            <b-spinner v-if="sos_slug_loading" small></b-spinner>
            Save link
          </b-button>
        </div>
        <div v-if="sos_slug_url" class="d-flex align-items-center" style="gap: 8px; flex-wrap: wrap; margin-top: 10px; font-weight: normal;">
          <b-form-input :value="sos_slug_url" readonly style="max-width: 520px;" @focus="$event.target.select()"></b-form-input>
          <b-button size="sm" variant="outline-secondary" @click="copy_sos_link">{{ sos_copied ? 'Copied!' : 'Copy' }}</b-button>
        </div>
      </div>
    </div>
    <div v-else>
      <br/><br/>
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

  <div class="tc-sticky-save">
    <transition name="tc-save-slide">
    <div v-if="isDirty || saving" class="d-flex align-items-center justify-content-center">
      <span v-if="isDirty && !saving" class="tc-unsaved-label mr-3" @click="scrollToFirstDirtyField()" title="Click to scroll to unsaved field" style="cursor:pointer">&#9679; Unsaved changes</span>
      <b-btn size="lg" variant="success" @click="save_config" :disabled="saving">
          <b-spinner v-if="saving" small class="mr-1"></b-spinner>
          {{ saving ? 'Saving...' : 'Save' }}
        </b-btn>
      &nbsp;&nbsp;
      <b-btn size="lg" variant="secondary" @click="load_data(true)">Discard Changes</b-btn>
    </div>
    </transition>
  </div>
</div>

</template>
<script>
import {get_tenant_config_settings, update_tenant_config_settings, set_sos_form_slug} from '../networking/apis'
import filterValidationMixin from '../mixins/filter_validation_mixin'
import {mapState} from "vuex";
import numericValidationMixin from '../mixins/numeric_validation_mixin'
import dirtyTrackingMixin from '../mixins/dirty_tracking_mixin'

export default {
  name: 'tenant_config',
  mixins: [filterValidationMixin, numericValidationMixin, dirtyTrackingMixin],
  components: {
  },

  data() {
    return {
      add_yard_model: {},
      manage_sos_yards: false,
      model: {},
      general_model: {},
      general_advanced_model: {},
      toggle: {},
      summary: {},
      loading: true,
      isNotSysadmin: false,
      isSamaraType: false,
      yardsLength: 0,
      objectKeyList: [],
      slug_input: '',
      sos_slug_url: null,
      sos_slug_loading: false,
      sos_copied: false,
      boolean_options: [
        { text: 'True', value: 'True' },
        { text: 'False', value: 'False' },
      ],
    }
  },
  async mounted() {
    this.load_data()
  },
  methods: {
    async load_data(silent = false) {
      if (!silent) this.loading = true
      let loaded = false
      try {
        const {value: { data = [] } } = await get_tenant_config_settings(this.user.tenant)
        this.summary = data.summary
        this.model = data.results

        try {
          const entries = Object.entries(this.model);
          for (let [key,value] of entries) {
            if (this.isArrayType(value) || this.isPlainObject(value,key)) {
              let setting = JSON.stringify(value)
              //console.log('found plain object or array: key:'+key+ ' value: '+ setting);
              this.model[key] = setting
              this.objectKeyList.push(key)
            }
          }
        } catch(e) {
          console.log('error looping through keys',e)
        }
        this.init_sos_slug()
        loaded = true
      } catch(e) {
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }

      this.loading = false
      if (loaded) this._resetDirtyTracking()
    },
    async save_config() {
      try {
        this.saving = true
        this.flushNumericValidation()
        this.flushFilterValidation()
        if (this.hasNumericErrors()) {
          this.$emit('showAlert', 'Fix the highlighted field before saving', 'danger', 6000)
          this.scrollToFirstNumericError()
          return
        }
        if (this.hasFilterErrors()) {
          this.$emit('showAlert', 'Fix the highlighted filter field before saving', 'danger', 6000)
          this.scrollToFirstFilterError()
          return
        }
        // Compute dirty keys before parsing (model still holds JSON strings matching snapshot).
        const dirtyKeys = new Set(Object.keys(this._dirtyFields()))
        if (dirtyKeys.size === 0) {
          this.$emit('showAlert', 'No changes to save', 'info', 3000)
          return
        }
        this.$emit('showAlert', 'Saving Changes', 'info',5000)
        const fullPayload = Object.assign({}, this.model)
        for (const objkey of this.objectKeyList) {
          try {
            if (fullPayload[objkey] && fullPayload[objkey].trim && fullPayload[objkey].trim() !== '') {
              fullPayload[objkey] = JSON.parse(fullPayload[objkey])
            }
          } catch (e) {
            this.$emit('showAlert', 'Invalid JSON in field: ' + objkey + ' — ' + e.message, 'danger', 8000)
            this.load_data()
            return
          }
        }
        const payload = {}
        for (const key of dirtyKeys) {
          if (key in fullPayload) payload[key] = fullPayload[key]
        }

        await update_tenant_config_settings(this.user.tenant, payload)
        this._resetDirtyTracking()
        await this.load_data(true)
        this.$emit('showAlert', 'Configuration Saved', 'success',5000)
      } catch(e) {
        this.$emit('showAlert', this._saveErrorMessage(e), 'danger', 5000)
      }
      finally {
        this.saving = false
      }
    },
    set_tenant_id(id) {
      this.user.tenant = id
      this.load_data()
    },
    update_toggle_div(item,field,setting) {
      if (!item[field] && setting) {
        item[field] = !item[field];
        this.$forceUpdate();
      } else if (item[field] && !setting) {
        item[field] = !item[field];
        this.$forceUpdate();
      }
    },
    toggle_div(item,field) {
      item[field] = !item[field];
      this.$forceUpdate();
    },
    isPlainObject(value, key) {
      //console.log('isPlainObject, value:' + value + ', type of value:' + (typeof value))
      let objtype = (typeof value)
      if (objtype == "object") {
        if (value != null && !Array.isArray(value)) {
          return true
        }
        return false
      }
    },
    isArrayType(value) {
      return Array.isArray(value)
    },
    init_sos_slug() {
      const slug = this.model && this.model.bus_sos_slug
      if (slug) {
        this.slug_input = slug
        this.sos_slug_url = 'https://web.fleet-integrations.com/#/sos/' + slug
      } else {
        // default the field to the company-name slug ("Bus Company" -> "bus-company") so the admin
        // can accept it with one click; the live URL only shows once a slug is actually saved
        this.slug_input = (this.model && this.model.bus_sos_slug_default) || ''
        this.sos_slug_url = null
      }
    },
    async save_sos_slug() {
      if (!this.slug_input) return
      const changing = this.model.bus_sos_slug && this.model.bus_sos_slug !== this.slug_input
      if (changing && !window.confirm('Change the link name? The current link will stop working for anyone who has it bookmarked.')) return
      this.sos_slug_loading = true
      try {
        const {value: {data = {}}} = await set_sos_form_slug(this.user.tenant, this.slug_input)
        this.slug_input = data.slug
        this.sos_slug_url = data.url
        this.model.bus_sos_slug = data.slug
        this.$emit('showAlert', 'Driver SOS link saved. Share it with your drivers.', 'success', 5000)
      } catch (e) {
        this.$emit('showAlert', this._saveErrorMessage ? this._saveErrorMessage(e) : 'Could not save the driver SOS link.', 'danger', 6000)
      } finally {
        this.sos_slug_loading = false
      }
    },
    copy_sos_link() {
      if (!this.sos_slug_url) return
      try {
        navigator.clipboard.writeText(this.sos_slug_url)
        this.sos_copied = true
        setTimeout(() => { this.sos_copied = false }, 2000)
      } catch (e) {
        this.$emit('showAlert', 'Copy failed — select the link and copy it manually.', 'warning', 5000)
      }
    },
  } ,
  computed:{
    ...mapState({
      user: state => state.profile.user
    }),
    is_admin() {
      return this.user.role === 'admin'
    },
    telematics_type() {
      return this.user.telematics_types[this.user.tenant]
    },
    tenant_type() {
      return this.user.types[this.user.tenant]
    },
    isBusSosEnabled() {
      const v = this.model.bus_sos_enabled
      return v === true || v === 'True'
    },
    isYardOnly() {
      // get_config stringifies scalars, so the flag arrives as 'True'/'False'
      const v = this.model.bus_sos_yard_only
      return v === true || v === 'True'
    },
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.load_data()
      }
    }
  }
}
</script>
<style>
@import "../../node_modules/@syncfusion/ej2-base/styles/bootstrap5.css";
@import "../../node_modules/@syncfusion/ej2-inputs/styles/bootstrap5.css";
@import "../../node_modules/@syncfusion/ej2-lists/styles/bootstrap5.css";
@import "../../node_modules/@syncfusion/ej2-popups/styles/bootstrap5.css";
@import "../../node_modules/@syncfusion/ej2-buttons/styles/bootstrap5.css";
@import "../../node_modules/@syncfusion/ej2-navigations/styles/bootstrap5.css";
@import "../../node_modules/@syncfusion/ej2-splitbuttons/styles/bootstrap5.css";
@import "../../node_modules/@syncfusion/ej2-richtexteditor/styles/bootstrap5.css";
</style>
<style scoped>
/* yard-only provider context block above the settings table */
.tc-sos-yard-only {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  max-width: 900px;
  margin: 0 0 10px 20px;
  font-size: 14px;
  font-weight: normal;
  color: #47566a;
}
.tc-sos-badge {
  flex: 0 0 auto;
  display: inline-block;
  padding: 3px 9px;
  border-radius: 999px;
  background: #e8eef5;
  color: #23608f;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}
</style>

